import React, { ReactElement, useEffect, useMemo, useState } from 'react';

import Widget from 'domain/entity/widget';
import { LayoutType, ConstraintsPerLayoutElement } from 'domain/entity/layout';
import { GridLayoutElement, GridLayouts } from 'adapter/in/dashboards/components/generic/ReactGrid';
import Grid, { A4_ROWS_PER_PAGE, CELL_MARGIN } from 'adapter/in/dashboards/components/Grid';
import { useGridView } from 'adapter/in/dashboards/containers/Grid/useGridView';

import StyledGridSize from 'adapter/in/dashboards/containers/Grid/style';
import { computePageBreakPositions } from 'adapter/in/dashboards/components/generic/ReactGrid/utils';
import Dashboard from 'domain/entity/dashboard';
import { Option } from 'fp-ts/lib/Option';
import { FrameWidgetData } from 'adapter/in/reports/components/CommonReportGrid/CommonReportGrid';

export enum GridOrigin {
    REPORT = 'REPORT',
    DASHBOARD = 'DASHBOARD',
}
export type GridContainerProps = {
    dashboardId: string;
    isEditEnabled: boolean;
    editingLayout: boolean;
    gridLayouts: GridLayouts;
    allGridLayouts: GridLayouts;
    widgetMap: Map<string, Widget>;
    selectedLayoutType: LayoutType;
    updateFrameDependencies?: {
        updateFrameData: Option<Dashboard>;
        updateFrameIsLoading: boolean;
    };
    frameData?: FrameWidgetData;
    gridOrigin?: GridOrigin;
    isReportInstance?: boolean;
};

const GridContainer = (props: GridContainerProps): ReactElement => {
    const {
        dashboardId,
        frameData,
        updateFrameDependencies,
        editingLayout,
        isEditEnabled,
        gridLayouts,
        allGridLayouts,
        selectedLayoutType,
        widgetMap,
        gridOrigin = GridOrigin.DASHBOARD,
        isReportInstance,
    } = props;

    const wrongSizeElements = useMemo(() => {
        const wrongSizeGridElementsWorker: Record<LayoutType, Record<string, ConstraintsPerLayoutElement>> = {
            [LayoutType.DESKTOP]: {},
            [LayoutType.TABLET]: {},
            [LayoutType.MOBILE]: {},
            [LayoutType.A4]: {},
        };
        if (Object.keys(allGridLayouts).length > 0) {
            Object.keys(allGridLayouts).forEach((layoutType) => {
                allGridLayouts[layoutType].forEach((gridLayoutElement) => {
                    if (!elementFitsContraints(gridLayoutElement)) {
                        wrongSizeGridElementsWorker[layoutType as LayoutType][gridLayoutElement.i] = {
                            type: layoutType as LayoutType,
                            minHeight: gridLayoutElement.minHeight,
                            minWidth: gridLayoutElement.minWidth,
                            maxHeight: gridLayoutElement.maxHeight,
                            maxWidth: gridLayoutElement.maxWidth,
                        };
                    }
                });
            });
        }
        return wrongSizeGridElementsWorker;
    }, [allGridLayouts]);

    const {
        cellSize,
        backgroundStyle,
        backgroundColor,
        currentBreakpoint,
        breakpoints,
        containerWidth,
        editLayoutWidthStyle,
        layoutTotalRows,
        onDragStart,
        onDragStop,
        onResize,
        onResizeStop,
        onWidthChange,
        onBreakpointChange,
        onLayoutChange,
        widgetBeingResized,
        updatedGridLayoutsWithFrame,
    } = useGridView(gridLayouts, allGridLayouts, editingLayout, selectedLayoutType, isEditEnabled, wrongSizeElements);

    const [pageBreakPositions, setPageBreakPositions] = useState<number[]>([]);

    useEffect(() => {
        if (currentBreakpoint === LayoutType.A4 && layoutTotalRows) {
            const rowPx = cellSize.height + CELL_MARGIN;
            const totalPages = Math.floor(layoutTotalRows / A4_ROWS_PER_PAGE);
            setPageBreakPositions(computePageBreakPositions(totalPages, rowPx, A4_ROWS_PER_PAGE));
            return;
        }
        setPageBreakPositions([]);
    }, [gridLayouts, currentBreakpoint, CELL_MARGIN, cellSize.height, layoutTotalRows]);

    return (
        <StyledGridSize $editLayoutWidthStyle={editLayoutWidthStyle} $backgroundColor={backgroundColor} $marginY={CELL_MARGIN}>
            {currentBreakpoint === LayoutType.A4 && (
                <div className='page-break-overlay'>
                    {pageBreakPositions.map((top, i) => (
                        <div key={i} className='page-break-line' style={{ top }} />
                    ))}
                </div>
            )}
            <Grid
                dashboardId={dashboardId}
                frameData={frameData}
                updateFrameDependencies={updateFrameDependencies}
                gridOrigin={gridOrigin}
                widgetMap={widgetMap}
                gridLayouts={gridLayouts}
                updatedGridLayoutsWithFrame={updatedGridLayoutsWithFrame}
                isEditEnabled={isEditEnabled}
                cellSize={cellSize}
                backgroundStyle={backgroundStyle}
                currentBreakpoint={currentBreakpoint}
                breakpoints={breakpoints}
                containerWidth={containerWidth}
                onResize={onResize}
                onDragStart={onDragStart}
                onResizeStop={onResizeStop}
                onDragStop={onDragStop}
                onWidthChange={onWidthChange}
                onBreakpointChange={onBreakpointChange}
                onLayoutChange={onLayoutChange}
                wrongSizeElements={wrongSizeElements}
                widgetBeingResized={widgetBeingResized}
                isReportInstance={isReportInstance}
            />
        </StyledGridSize>
    );
};

export default GridContainer;

// Utils

export const elementFitsContraints = (layoutElement: GridLayoutElement): boolean =>
    layoutElement.h <= layoutElement.maxHeight &&
    layoutElement.w <= layoutElement.maxWidth &&
    layoutElement.h >= layoutElement.minHeight &&
    layoutElement.w >= layoutElement.minWidth;
